import React, { useMemo } from 'react';
import { platformService } from '../../services/platform.service';
import { InlineComboCell } from './InlineComboCell';
import { committing } from './InlineFieldCells';
import { CategoryRecord, buildCategoryOptions, optionName } from './inlineCategoryOptions';

/** The ticket fields a picker cell can set - each a tiered list of records with Id, Value and ParentId. */
export type PickerField = 'CategoryId' | 'PriorityId' | 'TicketTypeId';

interface Props {
  ticketId: number;
  field: PickerField;
  /** The column's name. */
  label: string;
  valueId: number | null;
  /** What the column shows today, "No Category" and the like included. */
  displayName: string;
  records: CategoryRecord[];
  /** Asked again on the server after a save, so a ticket the view no longer matches leaves it. */
  onSaved: () => void | Promise<void>;
  /** Runs before the save and throws InlineEditRefused to stop it - for rows that cannot say if the ticket is closed. */
  guard?: () => Promise<void>;
}

/** Category, Priority or Type in the ticket list, typable in place. */
export const InlinePickerCell = ({ ticketId, field, label, valueId, displayName, records, onSaved, guard }: Props) => {
  // An archived value is named as the row names it, so the box does not open empty.
  const options = useMemo(() => {
    const built = buildCategoryOptions(records);
    return valueId != null && displayName && !built.some(option => option.id === valueId)
      ? [...built, { id: valueId, name: displayName, held: true }]
      : built;
  }, [records, valueId, displayName]);
  const nameOf = (id: number | null) => (id === valueId ? displayName : optionName(options, id));

  const commit = committing<number>(id => new platformService().updateTicket(ticketId, { [field]: id }).then(() => undefined), onSaved, guard);

  return (
    <InlineComboCell
      ticketId={ticketId}
      valueId={valueId}
      options={options}
      label={label}
      restingTitle={nameOf}
      renderResting={id => (
        <div className="w-full truncate" dir="rtl">
          <bdi>{nameOf(id)}</bdi>
        </div>
      )}
      commit={commit}
    />
  );
};
